/*----------------------------------
- DOMAINING NEWS ARTICLE
----------------------------------*/

/*----------------------------------
- DEPENDENCIES
----------------------------------*/

import { useEffect } from 'preact/hooks';

import type { LandingHeaderDomainsNavigation } from '@/client/catalogs/landing/pageContent';
import { LANDING_WEBSITE_LINKS } from '@/client/catalogs/landing/publicLinks';
import { appErrorReporter } from '@/common/libs/errorReporting';
import PageGeoBlock from '@/client/pages/landing/components/geo/PageGeoBlock';
import { buildDomainingNewsEditionGeoContext } from '@/client/pages/landing/components/geo/pageGeoContexts';
import { Footer } from '@/client/pages/landing/components/layout/Footer';
import { Header } from '@/client/pages/landing/components/layout/Header';
import { track } from '@/client/pages/landing/lib/analytics';
import { trackLandingCta } from '@/client/pages/landing/lib/trackLandingCta';
import { EMPTY_LANDING_NAVIGATION } from '@/client/pages/landing/types';
import {
    DOMAINING_NEWS_FEED_PATH,
    DOMAINING_NEWS_INDEX_PATH,
    domainingNewsEditionMarkdownPath,
    domainingNewsEditionPath,
    type PublishedNewsEdition,
} from '@uniquedomains/components/contracts/news/public';
import DomainingNewsArticle from '@uniquedomains/components/news/DomainingNewsArticle';
import DomainingNewsRail from '@uniquedomains/components/news/DomainingNewsRail';
import NewsShareActions, {
    type NewsShareAction,
} from '@uniquedomains/components/news/NewsShareActions';
import { buildDomainingNewsSections } from '@uniquedomains/components/news/sections';

import DomainingNewsSubscribeBand from './components/DomainingNewsSubscribeBand';

import '../../shared.css';

/*----------------------------------
- TYPES
----------------------------------*/

type DomainingNewsArticlePageProps = {
    canonicalUrl: string;
    edition: PublishedNewsEdition;
    navigation?: LandingHeaderDomainsNavigation;
};

/*----------------------------------
- CONSTANTS
----------------------------------*/

/** The phone action bar scrolls to the capture closing the edition. */
const SUBSCRIBE_ANCHOR_ID = 'subscribe';

/*----------------------------------
- COMPONENT
----------------------------------*/

export default function DomainingNewsArticlePage({
    canonicalUrl,
    edition,
    navigation = EMPTY_LANDING_NAVIGATION,
}: DomainingNewsArticlePageProps) {
    const geoContext = buildDomainingNewsEditionGeoContext({
        canonicalPath: domainingNewsEditionPath(edition.dateKey),
        headline: edition.headline,
        markdownMirrorPath: domainingNewsEditionMarkdownPath(edition.dateKey),
        publishedOn: edition.dateKey,
    });

    useEffect(() => {
        track('page_view', {
            page: 'domaining_news_edition',
            path: window.location.pathname + window.location.search,
            editionDate: edition.dateKey,
            sourceCount: edition.sourceCount,
        });
    }, [edition.dateKey, edition.sourceCount]);

    const trackCitation = (sourceNumber: number, href: string) => {
        trackLandingCta({
            page: 'domaining_news_edition',
            section: 'article_sources',
            label: `Source ${sourceNumber}`,
            kind: 'citation',
            href,
            action: 'open_original_article',
            surface: 'domaining_news',
        });
    };

    const trackShare = (action: NewsShareAction, href: string) => {
        trackLandingCta({
            page: 'domaining_news_edition',
            section: 'article_share',
            label: action,
            kind: 'share',
            href,
            action,
            surface: 'domaining_news',
        });
    };

    return (
        <div class="ud-landing-shell min-h-screen text-slate-950">
            <div class="relative z-10">
                <Header
                    links={LANDING_WEBSITE_LINKS}
                    navigation={navigation}
                    currentPath={DOMAINING_NEWS_INDEX_PATH}
                    trackingPage="domaining_news_edition"
                    mobileActions={{
                        secondary: {
                            label: 'RSS',
                            href: DOMAINING_NEWS_FEED_PATH,
                            external: true,
                            testId: 'domaining-news-mobile-rss',
                        },
                        primary: {
                            label: 'Get the Monday brief',
                            href: `#${SUBSCRIBE_ANCHOR_ID}`,
                            testId: 'domaining-news-mobile-subscribe',
                        },
                    }}
                />

                <main id="top">
                    <DomainingNewsArticle
                        edition={edition}
                        archiveHref={DOMAINING_NEWS_INDEX_PATH}
                        editionHref={domainingNewsEditionPath}
                        onCitationOpen={trackCitation}
                        shareActions={(
                            <NewsShareActions
                                canonicalUrl={canonicalUrl}
                                headline={edition.headline}
                                onShare={trackShare}
                                onError={(error) => appErrorReporter.app.handleError(error)}
                            />
                        )}
                        rail={(
                            <DomainingNewsRail
                                edition={edition}
                                archiveHref={DOMAINING_NEWS_INDEX_PATH}
                                sections={buildDomainingNewsSections(edition)}
                                shareActions={(
                                    <NewsShareActions
                                        canonicalUrl={canonicalUrl}
                                        headline={edition.headline}
                                        onShare={trackShare}
                                        onError={(error) => appErrorReporter.app.handleError(error)}
                                    />
                                )}
                            />
                        )}
                    />

                    <section
                        id={SUBSCRIBE_ANCHOR_ID}
                        class="mx-auto w-full max-w-[1160px] scroll-mt-24 px-5 pb-16 lg:px-8"
                    >
                        {/* Aligned under the article column, past the 250px reading rail. */}
                        <div class="lg:ml-[330px] lg:max-w-[760px]">
                            <DomainingNewsSubscribeBand source="website_news_edition" />
                        </div>
                    </section>

                    <PageGeoBlock className="pb-4" geo={geoContext} surface="domaining_news_edition" />
                </main>

                <Footer links={LANDING_WEBSITE_LINKS} sectionHrefPrefix="/" trackingPage="domaining_news_edition" />
            </div>
        </div>
    );
}
